// Share / Export for every list in the app — one PDF and one Excel builder that
// any page feeds with the columns it already renders on screen, so a report
// never needs its own exporter (the same idea as SharpAccounting's
// ReportShareDialog, rebuilt for this app's plain-CSS UI).
//
// A page passes:
//   title     "Guests"                      - the report's name
//   subtitle  "Sahil weds Diksha · Wedding" - event / filter line under it
//   columns   [{ key, label, align?, width?, render? }]
//   rows      the array it is already showing
//   totalsRow optional { [key]: value } printed as a bold last line
//   countText optional "11 guests · 48 persons" - what the rows COUNT, when
//             the number of lines is not the number of people
//   isGroupHead optional (row) => boolean - that line STARTS a block (a
//             household's head): printed bold with a rule above it
//
// A report may instead be SEVERAL blocks under one heading (the Brief Report):
//   sections  [{ title, note?, columns, rows, totalsRow? }]
//   summary   [{ label, value }] - the headline numbers, printed above them
// In the PDF each block is a table of its own under its heading; in Excel each
// block is a WORKSHEET of its own, with the summary as the first sheet.
//
// `render` may return JSX (the screen's chips and links): cellText() walks it
// down to plain text, so a page never has to keep a second set of columns.
//
// jsPDF / xlsx are ~1 MB together and most sessions never export, so both are
// loaded with import() the first time someone opens the dialog.

export const PDF_MIME = 'application/pdf';
export const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// ── A cell's plain text, whatever the column renders on screen ──────────────
const nodeText = (node) => {
  if (node === null || node === undefined || node === false || node === true) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (node.props) {
    if (node.props.children !== undefined) return nodeText(node.props.children);
    if (node.props.value !== undefined) return nodeText(node.props.value);
  }
  return '';
};

export const cellText = (col, row) => {
  if (col.exportValue) return String(col.exportValue(row) ?? '');
  const raw = col.render ? col.render(row) : row[col.key];
  const text = nodeText(raw);
  return text.replace(/\s+/g, ' ').trim();
};

// A column every row leaves empty is dropped: narrow paper, fewer columns.
const visibleColumns = (columns, rows) => (columns || []).filter(col => {
  if (col.exportHide) return false;
  if (!rows.length || col.keepEmpty) return true;
  return rows.some(row => cellText(col, row) !== '');
});

// One table, or the blocks of a multi-part report — the same shape either way.
const toBlocks = ({ columns, rows, totalsRow, sections }) => (
  Array.isArray(sections) && sections.length
    ? sections.map(s => ({ ...s, rows: s.rows || [], cols: visibleColumns(s.columns, s.rows || []) }))
    : [{ rows: rows || [], totalsRow, cols: visibleColumns(columns, rows || []) }]
);

export const safeFileName = (parts, ext) => {
  const base = parts.filter(Boolean).join(' ').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  return `${base || 'Report'}.${ext}`;
};

// When the report was taken - the DATE AND THE TIME, because two printouts of
// the same list an hour apart are different lists.
const stamp = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  const h = d.getHours();
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${h12}:${p(d.getMinutes())} ${h < 12 ? 'AM' : 'PM'}`;
};

// ── PDF ─────────────────────────────────────────────────────────────────────
// Text sizes of a printed report, in pt (the Share / Export "Text size" choice).
// Large print is for a list carried round and read at arm's length.
// `pad` is the space ABOVE and BELOW a line, `padX` the space beside the text.
// They are separate because only the vertical one costs lines on the page: at
// large print 2.2 mm top and bottom made a 9 mm row and 27 names a page, when
// the type itself is only 4.7 mm tall (owner, 2026-10-29 - "font should be
// this only, only reduce space vertically"). Thin vertical padding takes the
// same list to 45-ish lines with the type untouched; the horizontal padding
// keeps its old value, so nothing crowds the column borders.
export const REPORT_SIZES = {
  compact: { font: 6.6, pad: 0.45, padX: 0.8, head: 6.8, margin: 7, titleGap: 4 },
  normal: { font: 9, pad: 0.6, padX: 1.5, head: 9, margin: 10, titleGap: 5 },
  large: { font: 11.5, pad: 0.75, padX: 2.2, head: 11.5, margin: 10, titleGap: 6 },
};
export const REPORT_SIZE_OPTIONS = [['compact', 'Small'], ['normal', 'Medium'], ['large', 'Large print']];

// Which pages to keep: "3", "1,4", "2-5", "1,3-4" - blank, or anything that
// makes no sense, means ALL of them (a print must never come out empty
// because of a typo). Numbers outside the report are dropped, not an error:
// asking for 1-99 of a two-page list prints the two pages.
export function parsePageRange(text, total) {
  const all = Array.from({ length: Math.max(0, total) }, (_, i) => i + 1);
  const raw = String(text || '').trim();
  if (!raw) return all;
  const keep = new Set();
  raw.split(/[,;\s]+/).filter(Boolean).forEach((part) => {
    const m = /^(\d+)(?:\s*[-–]\s*(\d+))?$/.exec(part.trim());
    if (!m) return;
    const from = Number(m[1]);
    const to = m[2] === undefined ? from : Number(m[2]);
    for (let n = Math.min(from, to); n <= Math.max(from, to); n += 1) {
      if (n >= 1 && n <= total) keep.add(n);
    }
  });
  return keep.size ? [...keep].sort((a, b) => a - b) : all;
}
export async function buildReportPdf({
  title, subtitle, note, columns, rows, totalsRow, sections, summary, countText, isGroupHead,
  orientation = 'portrait', organiserName, eventName,
  density = 'compact',          // compact = most rows per page · normal = bigger · large = large print
  pages,                        // "1,3-4" - blank / nonsense = every page
  // A list worked through at a table (the sweets packing list): at least this
  // many lines on every page, the text as big as that leaves room for, a
  // one-line header and nothing wrapping. Overrides the text size chosen.
  rowsPerPage,
  onInfo,                       // told how many pages the WHOLE report has
}) {
  const d = rowsPerPage
    ? { ...(REPORT_SIZES[density] || REPORT_SIZES.compact), margin: 8, titleGap: 2 }
    : (REPORT_SIZES[density] || REPORT_SIZES.compact);
  // Header type: a fixed-lines list keeps the header small so the lines get the page.
  const titlePt = rowsPerPage ? 11 : d.font + 2.4;
  const smallPt = rowsPerPage ? 8 : d.font;
  // Line height in mm for a font size in pt (1 pt = 0.353 mm, plus leading),
  // so the header lines never run into each other at the larger sizes.
  const lh = (pt) => pt * 0.48;
  const { default: JsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new JsPDF({ orientation, unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const blocks = toBlocks({ columns, rows, totalsRow, sections });
  const rowCount = blocks.reduce((n, b) => n + b.rows.length, 0);

  // Header: who it belongs to, what the list is, when it was taken.
  // One header line instead of four: name of the event, what the list is and
  // its filters on the left, when it was taken and how many rows on the right.
  // The NAME of the list is one short bold line (event + what it is); the
  // filters - which side, whose list, which party - go under it in the plain
  // body size. Spelling them all in the big bold type took three lines at
  // large print and broke "Ailani Lavina - 9822009979" across two of them.
  const left = [eventName || organiserName || 'Mehfil', title].filter(Boolean).join('  ·  ');
  const details = String(subtitle || '').trim();
  // `countText` lets a list say what it really holds - "11 guests · 48
  // persons" - because a count-only row ("Gym Friends · 9") is one line and
  // nine people, and the line count alone would under-report the party.
  const right = `${countText || `${rowCount} ${rowCount === 1 ? 'row' : 'rows'}`}  ·  ${stamp()}`;
  const usableW = pageW - 2 * d.margin;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(smallPt);
  const rightW = doc.getTextWidth(right);
  let y = 4 + lh(titlePt);
  doc.setTextColor(90);
  doc.text(right, pageW - d.margin, y, { align: 'right' });
  // A long title wraps under itself instead of running into the date.
  doc.setFont('helvetica', 'bold'); doc.setFontSize(titlePt); doc.setTextColor(0);
  const leftLines = doc.splitTextToSize(String(left), Math.max(usableW - rightW - 4, usableW * 0.4));
  leftLines.forEach((line, i) => { if (i) y += lh(titlePt); doc.text(line, d.margin, y); });
  if (details) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(smallPt); doc.setTextColor(70);
    // The whole width: the date and the counts sit on the first line only.
    doc.splitTextToSize(details, usableW).forEach((line) => { y += lh(smallPt) + 0.4; doc.text(line, d.margin, y); });
    doc.setTextColor(0);
  }
  if (note) {
    doc.setFont('helvetica', 'italic'); doc.setFontSize(smallPt); doc.setTextColor(120);
    // A fixed-lines list says its note on ONE line (the parts joined with ·).
    const noteText = rowsPerPage ? String(note).split('\n').join('  ·  ') : String(note);
    doc.splitTextToSize(noteText, usableW).forEach(line => { y += lh(smallPt) + 0.6; doc.text(line, d.margin, y); });
  }
  // The headline numbers of a brief: one line, so they are read before the tables.
  if (Array.isArray(summary) && summary.length) {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(d.font + 0.8); doc.setTextColor(25);
    doc.splitTextToSize(summary.map(s => `${s.label}: ${s.value}`).join('    ·    '), usableW)
      .forEach(line => { y += lh(d.font + 0.8) + 0.6; doc.text(line, d.margin, y); });
  }
  doc.setTextColor(0);

  const usable = pageW - 2 * d.margin;

  // Columns with a pdfWidth (mm) keep their proportions; when they add up to
  // more than the page (portrait, many functions) they are all scaled down.
  const drawTable = (block, startY) => {
    const cols = block.cols;
    const fixed = cols.reduce((s, c) => s + (Number(c.pdfWidth) || 0), 0);
    const autoCount = cols.filter(c => !c.pdfWidth).length;
    const autoRoom = autoCount * 36;   // what each column without a width is left at least
    // All columns sized → stretch or shrink them to exactly the page width.
    const scale = fixed > 0 && autoCount === 0 ? usable / fixed
      : fixed > 0 && fixed > usable - autoRoom ? Math.max(usable - autoRoom, 10) / fixed : 1;

    const body = block.rows.map(row => cols.map(col => cellText(col, row)));
    // Which body lines START a block (a household's head), so a reader's eye
    // can find where one family stops and the next begins.
    const heads = new Set(isGroupHead ? block.rows.map((r, i) => (isGroupHead(r) ? i : -1)).filter(i => i >= 0) : []);
    if (block.totalsRow) body.push(cols.map(col => String(block.totalsRow[col.key] ?? '')));

    // No word is ever broken: a column is at least as wide as its longest word
    // (a mobile number, "Ceremony" in a function's heading). When every column
    // is sized and those minimums do not fit the page at this text size, the
    // text steps down half a point at a time until they do - large print stays
    // as large as the page allows, and never splits a phone number in two.
    let fontPt = d.font;
    let headPt = d.head;
    let widths = null;
    // At least `rowsPerPage` lines on the FIRST page (the later ones, with no
    // report header, then hold a few more): the row height is what the page
    // leaves divided by the lines, and the text is as big as that row allows.
    // A row = the text line (pt × 0.3528 mm × autotable's 1.15) + its padding.
    let pad = d.pad;
    const padX = d.padX ?? d.pad;
    const fit = rowsPerPage && block.fitRows !== false;
    if (fit) {
      pad = 0.45;
      const rowH = (pt) => pt * 0.3528 * 1.15 + 2 * pad;
      const bottom = 6;
      let pt = 14;
      for (let i = 0; i < 3; i += 1) {
        const hpt = Math.min(pt, 10);
        const avail = pageH - startY - bottom - rowH(hpt) - 0.5;
        pt = Math.min(14, ((avail / rowsPerPage) - 2 * pad) / (0.3528 * 1.15));
      }
      fontPt = Math.floor(pt * 10) / 10;
      headPt = Math.min(fontPt, 10);
    }
    if (fixed > 0 && autoCount === 0) {
      const wordsOf = (v) => String(v ?? '').split(/\s+/).filter(Boolean);
      const minWidths = (pt, hpt, headWords = true) => cols.map((col, i) => {
        let w = col.tick ? 3 : 0;
        if (headWords) {
          doc.setFont('helvetica', 'bold'); doc.setFontSize(hpt);
          wordsOf(col.label).forEach(word => { w = Math.max(w, doc.getTextWidth(word)); });
        }
        doc.setFont('helvetica', 'normal'); doc.setFontSize(pt);
        // `nowrap`: the whole value stays on one line (a person's name repeated
        // down the page - two lines each would double every row's height).
        if (col.nowrap) body.forEach(r => { w = Math.max(w, doc.getTextWidth(String(r[i] ?? ''))); });
        else if (!col.tick) body.forEach(r => wordsOf(r[i]).forEach(word => { w = Math.max(w, doc.getTextWidth(word)); }));
        return w + padX * 2 + 1;
      });
      const sum = (a) => a.reduce((s, w) => s + w, 0);
      // The headings give way first (a long function name may take a smaller
      // font), then the body - but never more than 2.5 pt below the size chosen.
      const floor = Math.max(REPORT_SIZES.compact.font, fontPt - 2.5);
      let mins = minWidths(fontPt, headPt);
      while (sum(mins) > usable && headPt > floor) { headPt -= 0.5; mins = minWidths(fontPt, headPt); }
      while (sum(mins) > usable && fontPt > floor) { fontPt -= 0.5; mins = minWidths(fontPt, headPt); }
      // Still too wide (many functions on a portrait page): a heading may now
      // break inside a word, the body still never does.
      if (sum(mins) > usable) mins = minWidths(fontPt, headPt, false);
      // Each column gets its share of the page, but never less than its minimum;
      // what the narrow ones need is taken from the columns with room to spare.
      widths = cols.map((col, i) => Math.max(Number(col.pdfWidth) * scale, mins[i]));
      let over = widths.reduce((s, w) => s + w, 0) - usable;
      for (let pass = 0; pass < 4 && over > 0.1; pass += 1) {
        const slack = widths.map((w, i) => Math.max(0, w - mins[i]));
        const total = slack.reduce((s, v) => s + v, 0);
        if (!total) break;
        const cut = over;
        widths = widths.map((w, i) => w - cut * (slack[i] / total));
        over = widths.reduce((s, w) => s + w, 0) - usable;
      }
    }

    autoTable(doc, {
      startY,
      head: [cols.map(col => col.label)],
      body,
      theme: 'grid',
      // Dark, clearly visible grid lines - the light default disappears on a
      // printed page (and on a photocopy of it).
      // A fixed-lines list never wraps a cell (a second line would push the
      // page under its count): a too-long name is cut with "...".
      styles: { font: 'helvetica', fontSize: fontPt, cellPadding: { top: pad, bottom: pad, left: padX, right: padX }, overflow: fit ? 'ellipsize' : 'linebreak', textColor: 0, lineWidth: 0.25, lineColor: [40, 40, 40] },
      headStyles: { fillColor: [215, 225, 240], textColor: 0, fontStyle: 'bold', fontSize: headPt, lineWidth: 0.3, lineColor: [20, 20, 20] },
      alternateRowStyles: { fillColor: [249, 250, 252] },
      columnStyles: Object.fromEntries(cols.map((col, i) => [i, {
        halign: col.align === 'num' || col.align === 'right' ? 'right' : col.align === 'center' ? 'center' : 'left',
        cellWidth: widths ? widths[i] : col.pdfWidth ? Number(col.pdfWidth) * scale : 'auto',
      }])),
      // The totals line is the last body row: print it bold.
      didParseCell: (data) => {
        const isTotals = block.totalsRow && data.section === 'body' && data.row.index === body.length - 1;
        if (data.section === 'body' && !isTotals && heads.has(data.row.index)) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.lineWidth = { top: 0.5, right: 0.25, bottom: 0.25, left: 0.25 };
        }
        if (isTotals) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [238, 242, 248];
        }
        // A `tick` column: the PDF font has no ✓, so a cell holding one is left
        // blank here and the tick is drawn into it in didDrawCell. A cell that
        // holds anything ELSE - "9", because that line is nine people - keeps
        // its text and is simply printed.
        const col = cols[data.column.index];
        if (col && col.tick && data.section === 'body' && !isTotals && data.cell.raw === '✓') {
          data.cell.tick = true;
          data.cell.text = [''];
        }
      },
      didDrawCell: (data) => {
        if (!data.cell.tick) return;
        const { x, y: cy0, width, height } = data.cell;
        const s = Math.min(height * 0.55, 3);
        const cx = x + width / 2, cy = cy0 + height / 2;
        doc.setDrawColor(0, 95, 40); doc.setLineWidth(0.55);
        doc.line(cx - s * 0.5, cy, cx - s * 0.15, cy + s * 0.38);
        doc.line(cx - s * 0.15, cy + s * 0.38, cx + s * 0.55, cy - s * 0.42);
        doc.setDrawColor(0); doc.setLineWidth(0.1);
      },
      didDrawPage: () => {
        const page = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(rowsPerPage ? 7.5 : d.font - 0.6); doc.setTextColor(120);
        doc.text(`${title || 'Report'} — page ${page}`, pageW / 2, pageH - 3.5, { align: 'center' });
        doc.setTextColor(0);
      },
      margin: { left: d.margin, right: d.margin, top: rowsPerPage ? 8 : 10, bottom: rowsPerPage ? 6 : 7 },
    });
  };

  let cursor = y + d.titleGap;
  blocks.forEach((block, i) => {
    // `newPage` starts the block on a fresh sheet: that is what makes one file
    // of several lists printable AS several lists (the guest lists per set of
    // functions - "Reception only", "Wedding + Reception" ...).
    if (block.newPage && i > 0) { doc.addPage(); cursor = 10; }
    if (block.title) {
      // A heading is never left alone at the foot of a page.
      if (cursor > pageH - 28) { doc.addPage(); cursor = 12; }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(d.font + 1.8); doc.setTextColor(20);
      doc.text(String(block.title), d.margin, cursor + lh(d.font + 1.8));
      cursor += lh(d.font + 1.8) + 1.2;
      if (block.note) {
        doc.setFont('helvetica', 'italic'); doc.setFontSize(d.font); doc.setTextColor(120);
        doc.text(String(block.note), d.margin, cursor + lh(d.font));
        cursor += lh(d.font) + 0.6;
      }
      doc.setTextColor(0);
      cursor += 1;
    }
    drawTable(block, cursor);
    cursor = doc.lastAutoTable.finalY + (i < blocks.length - 1 ? 7 : 0);
  });

  // The whole report is drawn first - a page is only reached by filling the
  // ones before it - and the pages not asked for are then dropped, from the
  // back so the earlier numbers still mean what they did.
  const total = doc.getNumberOfPages();
  if (typeof onInfo === 'function') onInfo({ pageCount: total });
  const keep = new Set(parsePageRange(pages, total));
  if (keep.size < total) {
    for (let n = total; n >= 1; n -= 1) if (!keep.has(n)) doc.deletePage(n);
  }
  return doc.output('blob');
}

// ── Excel ───────────────────────────────────────────────────────────────────
// One worksheet out of one block of a report.
const blockSheet = (XLSX, block, head) => {
  const cols = block.cols;
  const width = Math.max(cols.length, 2);
  const pad = (first, second) => {
    const line = Array(width).fill('');
    line[0] = first;
    if (second !== undefined) line[1] = second;
    return line;
  };

  const aoa = [];
  head.forEach(line => aoa.push(pad(String(line))));
  if (block.title) aoa.push(pad(String(block.title)));
  if (block.note) aoa.push(pad(String(block.note)));
  aoa.push(Array(width).fill(''));

  const headerIndex = aoa.length;
  aoa.push(cols.map(col => col.label));
  block.rows.forEach(row => aoa.push(cols.map(col => {
    const text = cellText(col, row);
    // Keep numbers numeric so Excel can sum them; a mobile number stays text.
    if (col.align === 'num' && text && /^-?[\d,]+(\.\d+)?$/.test(text.replace(/[₹\s]/g, ''))) {
      return Number(text.replace(/[₹,\s]/g, ''));
    }
    return text;
  })));
  if (block.totalsRow) aoa.push(cols.map(col => block.totalsRow[col.key] ?? ''));

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = cols.map(col => ({
    wch: Math.min(Math.max(String(col.label).length + 4, col.excelWidth || 14), 42),
  }));
  // The title block spans the table so it reads like a report, not a stray cell.
  ws['!merges'] = Array.from({ length: Math.max(headerIndex - 1, 0) }, (_, r) => ({
    s: { r, c: 0 }, e: { r, c: Math.max(width - 1, 0) },
  }));
  ws['!freeze'] = { xSplit: 0, ySplit: headerIndex + 1 };
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: headerIndex, c: 0 },
      e: { r: headerIndex + block.rows.length, c: Math.max(width - 1, 0) },
    }),
  };
  return ws;
};

export async function buildReportExcel({
  title, subtitle, note, columns, rows, totalsRow, sections, summary, countText, organiserName, eventName,
}) {
  const XLSX = await import('xlsx');
  const blocks = toBlocks({ columns, rows, totalsRow, sections });
  const rowCount = blocks.reduce((n, b) => n + b.rows.length, 0);

  const head = [String(eventName || organiserName || 'Mehfil')];
  if (organiserName && eventName) head.push(String(organiserName));
  head.push(String(title || 'Report'));
  if (subtitle) head.push(String(subtitle));
  if (note) head.push(String(note));
  head.push(`Printed: ${stamp()}   ·   ${countText || `${rowCount} ${rowCount === 1 ? 'row' : 'rows'}`}`);

  const wb = XLSX.utils.book_new();

  // The headline numbers come first, on a sheet of their own, so the file
  // opens on the brief and the detail follows behind it.
  if (Array.isArray(summary) && summary.length) {
    const aoa = head.map(line => [String(line), '']);
    aoa.push(['', '']);
    summary.forEach(s => aoa.push([String(s.label), s.value]));
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 34 }, { wch: 22 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Brief');
  }

  const used = new Set();
  blocks.forEach((block, i) => {
    // Excel sheet names: 31 characters, unique, none of  : \ / ? * [ ]
    let name = String(block.title || title || 'Report').replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 28) || `Sheet ${i + 1}`;
    while (used.has(name.toLowerCase())) name = `${name.slice(0, 26)} ${i + 1}`;
    used.add(name.toLowerCase());
    XLSX.utils.book_append_sheet(wb, blockSheet(XLSX, block, blocks.length > 1 ? [head[0], head[head.length - 1]] : head), name);
  });

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([out], { type: EXCEL_MIME });
}

// ── Where the file goes ─────────────────────────────────────────────────────
export function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = fileName;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// Print: the PDF opens in a hidden frame and the browser's print dialog is
// asked for it, so what is printed is exactly what the PDF shows.
export function printPdfBlob(blob) {
  const url = URL.createObjectURL(blob);
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  frame.src = url;
  frame.onload = () => {
    try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch { window.open(url, '_blank'); }
  };
  document.body.appendChild(frame);
  setTimeout(() => { frame.remove(); URL.revokeObjectURL(url); }, 60000);
}
